import React, { forwardRef, type ReactNode, useMemo } from "react";
import { styled } from "@linaria/react";
import { t } from "@/themes/theme.ts";

interface DragBarProps {
  children?: ReactNode;
  dragCallback: (e: never) => void;
}

const DragBarWrapper = styled.div`
  // Hack: The bar itself visually is 1px (could be more if needed)
  // but it has more padding, as to allow dragging to become easier
  // Set max width to 1px as to prevent white/transparent space between drag bar and left side
  max-width: 1px;

  background-color: transparent;
  cursor: col-resize;
  display: flex;
  flex-direction: row-reverse;
  flex-shrink: 0;
  height: 100%;
  will-change: position;
`;

const DragBarCol = styled.div`
  display: flex;
  flex-shrink: 0;

  border-right: 1px solid ${t.brandBorderColor};
  width: 1px;
  padding-left: 1rem;
`;

const DragBar = forwardRef<HTMLDivElement, DragBarProps>((props, ref) => {
  const EmptyImage = useMemo(() => {
    if (typeof document !== "undefined") {
      const img = document.createElement("img");
      // 1 pixel GIF that's invisible
      // The reason for this is that chrome (and probably other browsers) will take 'none'/invalid url's as
      // 'lets show a huge and ugly image on the cursor instead'
      img.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

      return img;
    }
  }, []);

  const disableDefault = (
    e: React.DragEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement> | UIEvent,
  ) => {
    e.preventDefault();
    if ("dataTransfer" in e) e.dataTransfer.dropEffect = "none";
  };

  const disableImage = (
    e: React.DragEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement> | UIEvent,
  ) => {
    if ("dataTransfer" in e) {
      if (EmptyImage != null) e.dataTransfer.setDragImage(EmptyImage, 0, 0);
    }
  };

  return (
    <DragBarWrapper
      draggable={true}
      onTouchMove={props.dragCallback}
      onDrag={props.dragCallback}
      onDragOver={disableDefault}
      onDragStart={disableImage}
      ref={ref}
    >
      <DragBarCol>{props.children}</DragBarCol>
    </DragBarWrapper>
  );
});

DragBar.displayName = "DragBar";
export default DragBar;
